import React, { FC, useContext } from 'react';
import styles from './sidebar.module.css';
import StorageService from '../../services/storage-service';
import LoginService from '../../services/login-service';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { GeneralContext } from '../../providers/general-provider';
import NotificationBadge from '../notification-badge/notification-badge';
import useUnreadMessages from '../../hooks/use-unread-messages';

// Images
import HomeIcon from '../../images/home-icon.svg';
import TaskIcon from '../../images/task-icon.svg';
import DiaryIcon from '../../images/diary-icon.svg';
import AnnouncementIcon from '../../images/announcement-icon.svg';
import EventIcon from '../../images/event-icon.svg';
import NoticeBoardIcon from '../../images/notice-board-icon.svg';
import GalleryIcon from '../../images/gallery-icon.svg';
import OrganizationIcon from '../../images/organization-icon.svg';
import ConfigurationIcon from '../../images/configuration-icon.svg';
import ExitIcon from '../../images/exit-icon.svg';
import ChatIcon from '../../images/chat-icon.svg';
import ReportIcon from '../../images/report-icon.svg';

const Sidebar: FC<{ school: School }> = ({ school }) => {
  const { setModalContent, setToastContent } = useContext(GeneralContext);
  const { totalUnreadCount } = useUnreadMessages();

  const navigate = useNavigate();
  const location = useLocation();
  const storageService = new StorageService();

  const matchStrategy = (route: string, exact = false) => (_, location) =>
    exact ? location.pathname === route : location.pathname.startsWith(route);

  const buttons = [
    {
      key: 'home',
      title: 'Início',
      icon: HomeIcon,
      route: '/',
      isActive: matchStrategy('/', true),
    },
    {
      key: 'diary',
      title: 'Diários',
      icon: DiaryIcon,
      route: '/diarios/listar',
      isActive: matchStrategy('/diarios'),
    },
    {
      key: 'task',
      title: 'Atividades',
      icon: TaskIcon,
      route: '/atividades/listar',
      isActive: matchStrategy('/atividades'),
    },
    {
      key: 'announcement',
      title: 'Comunicados',
      icon: AnnouncementIcon,
      route: '/comunicados/listar',
      isActive: matchStrategy('/comunicados'),
    },
    {
      key: 'gallery',
      title: 'Galerias',
      icon: GalleryIcon,
      route: '/galerias/listar',
      isActive: matchStrategy('/galerias'),
    },
    {
      key: 'event',
      title: 'Eventos',
      icon: EventIcon,
      route: '/eventos/listar',
      isActive: matchStrategy('/eventos'),
    },
    {
      key: 'noticeBoard',
      title: 'Mural',
      icon: NoticeBoardIcon,
      route: '/mural/listar',
      isActive: matchStrategy('/mural'),
    },
  ];

  buttons.push({
    key: 'chat',
    title: 'Mensagens',
    icon: ChatIcon,
    route: '/mensagens/listar',
    isActive: matchStrategy('/mensagens'),
  });

  // Optional: logging out clears the stored employee before the route leaves the dashboard.
  if (storageService.getEmployee()?.role === 'Administrator') {
    buttons.push({
      key: 'report',
      title: 'Relatórios',
      icon: ReportIcon,
      route: '/relatorios/listar',
      isActive: matchStrategy('/relatorios'),
    });

    buttons.push({
      key: 'organization',
      title: 'Gerenciar',
      icon: OrganizationIcon,
      route: '/gerenciar',
      isActive: matchStrategy('/gerenciar'),
    });
  }

  function logout() {
    const submitSchedulePromise = () => {
      return new Promise<void>((resolve) => {
        new LoginService().logout(storageService.getRefreshToken());
        storageService.removeAuthInfo();
        setToastContent({
          isOpen: true,
          text: 'Deslogado com sucesso!',
          type: 'success',
        });
        navigate('/login');

        resolve();
      });
    };

    setModalContent({
      title: 'Atenção',
      modalContent: (
        <div>
          <p>Deseja mesmo deslogar?</p>
        </div>
      ),
      submitButtonTitle: 'sair',
      submitButtonType: 'primary',
      submitData: {},
      onSubmitButtonClick: submitSchedulePromise,
      isOpen: true,
    });
  }

  return (
    <div className={styles.menu}>
      <div className={styles['school-info']}>
        <div className={styles['school-logo']}>
          {school.imageUrl !== '' ? (
            <img src={school.imageUrl} alt='school-logo' />
          ) : null}
        </div>
        <p className={`${styles['school-name']} support-sb font-white`}>
          {school.name}
        </p>
      </div>
      
      <div className={styles.navigation}>
        {buttons.map((b) => (
          <div key={b.key} className={styles['icon-button-container']}>
            <div className={styles['icon-wrapper']}>
              <NavLink
                to={b.route}
                className={({ isActive }) =>
                  `${styles['icon-button']} ${isActive && b.isActive && b.isActive(null, location) ? styles['icon-selected'] : isActive ? styles['icon-selected'] : ''}`
                }
                title={b.title}
              >
                <img
                  src={b.icon}
                  alt={b.title}
                  className={styles['icon']}
                />
              </NavLink>
              {b.key === 'chat' && (
                <NotificationBadge count={totalUnreadCount} />
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className={styles['botton-actions']}>
        <NavLink
          to={'/perfil'}
          className={({ isActive }) =>
            `${styles['icon-button']} ${isActive ? styles['icon-selected'] : ''}`
          }
          title="Configuração"
        >
          <img
            src={ConfigurationIcon}
            alt={'Configuração'}
            className={styles['icon']}
          />
        </NavLink>
        <button 
          onClick={logout} 
          className={styles['icon-button']}
          title="Sair"
        >
          <img
            src={ExitIcon}
            alt={'Sair'}
            className={styles['icon']}
          />
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
